import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBars, FaSignInAlt, FaTimes, FaUserCircle, FaUserPlus } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../bootstrap/bootstrap-5.3.3-dist/css/bootstrap.min.css';
import API_URL from '../config';
import logo from '../images/logo.png';
import './courseHeaderLogin.css';

axios.defaults.withCredentials = true;

function HeaderLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  const [activeLink, setActiveLink] = useState(location.pathname);

  // ✅ Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/register/check`);
        if (res.data.isAuthenticated) {
          setIsLoggedIn(true);
          setIsAdmin(res.data.user?.isAdmin || false);
          setUser(res.data.user);
        } else {
          setIsLoggedIn(false);
          setIsAdmin(false);
        }
      } catch {
        setIsLoggedIn(false);
        setIsAdmin(false);
      }
    };
    checkAuth();
  }, []);

  // ✅ Sticky header
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.headerLogin');
      header?.classList.toggle('scrolled', window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const closeMenu = (e) => {
      if (!e.target.closest('.navbar')) setMenuOpen(false);
    };
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [menuOpen]);

  // ✅ Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ Scroll to section
  const handleScrollToSection = (hash) => {
    if (location.pathname !== '/') {
      navigate(`/${hash}`);
      setTimeout(() => {
        const section = document.querySelector(hash);
        section?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    } else {
      const section = document.querySelector(hash);
      section?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // ✅ Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/register/logout`);
      setIsLoggedIn(false);
      setIsAdmin(false);
      setUser(null);
      setShowProfileMenu(false);
      navigate('/courses');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // ✅ Nav items
  const navItems = [
    { label: t('Home'), path: '/' },
    { label: t('About'), path: '#about' },
    { label: t('Skills'), path: '#offers' },
    { label: t('Contact'), path: '#contactUs' },
    { label: t('Courses'), path: '/courses' },
  ];

  return (
    <header className="headerLogin shadow-sm">
      <nav className="navbar navbar-expand-lg navbar-dark py-2 px-3 w-100">
        <div className="container-fluid">
          {/* ✅ Logo */}
          <div
            className="d-flex align-items-center cursor-pointer"
            onClick={() => navigate(isAdmin && '/adminPanel' )}
          >
            <img src={logo} alt="Logo" id="headerLogo" className="me-2" />
          </div>

          {/* ✅ Toggle Button */}
          <button
            className="navbar-toggler border-0 p-2"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
          >
            {menuOpen ? <FaTimes size={26} /> : <FaBars size={26} />}
          </button>

          {/* ✅ Menu */}
          <div
            className={`collapse navbar-collapse ${menuOpen ? 'show' : ''}`}
            id="navbarMenu"
            style={{
              backdropFilter: menuOpen && "blur(10px)",
              position: menuOpen ? 'fixed ' : 'relative',
              top:  menuOpen ? 120 : 0,
              left: 0,
              width: menuOpen ? '100%' : 'auto',
              height: menuOpen ? 'min-content' : 'auto',
              zIndex: menuOpen ? 1050 : 'auto',
              padding: menuOpen ? '30px' : '0',
            }}
          >
            <ul className="navbar-nav mx-auto text-center my-3 my-lg-0 gap-lg-4">
              {navItems.map((item, index) => (
                <li
                  key={index}
                  className={`nav-item ${activeLink === item.path ? 'active' : ''}`}
                >
                  {item.path.startsWith('#') ? (
                    <a
                      href={item.path}
                      className="nav-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handleScrollToSection(item.path);
                        setMenuOpen(false);
                      }}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      className="nav-link"
                      to={item.path}
                      onClick={() => {
                        setActiveLink(item.path);
                        setMenuOpen(false);
                      }}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* ✅ Auth or Profile */}
            <div className="d-flex flex-column flex-lg-row align-items-center gap-3 text-center">
              {!isLoggedIn ? (
                <>
                  <Link to="/signup" className="btn auth-btn" onClick={() => setMenuOpen(false)}>
                    <FaUserPlus className="me-2" />
                    {t('SIGN UP')}
                  </Link>
                  <Link to="/login" className="btn auth-btn" onClick={() => setMenuOpen(false)}>
                    <FaSignInAlt className="me-2" />
                    {t('LOG IN')}
                  </Link>
                </>
              ) : (
                <div className="position-relative" ref={profileMenuRef}>
                  <FaUserCircle
                    className="profile-icon text-white"
                    size={34}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowProfileMenu((prev) => !prev)}
                  />
                  {showProfileMenu && (
                    <div className="profile-dropdown p-3 mt-2">
                      <p className="fw-bold mb-1">{user?.username || 'User'}</p>
                      <p className="small text-muted mb-2">{user?.email}</p>

                      <hr className="my-2" />
                      <button
                        className="dropdown-item text-start w-100"
                        onClick={() => {
                          navigate('/my-courses');
                          setShowProfileMenu(false);
                        }}
                      >
                        🎓 {t('My Paid Courses','كورساتي المدفوعه')}
                      </button>

                      <button
                        className="dropdown-item text-start w-100 text-danger"
                        onClick={handleLogout}
                      >
                        🚪 {t('Logout')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default HeaderLogin;
